import type {
  ActiveMic,
  MicChoice,
  MicResolution,
  MicSwapEvent,
  MicSwitchErrorCode,
  MicSwitchReason,
} from './types';

/**
 * Every microphone the recording engine opens goes through `openMic`, so the probe, both
 * recorders and the meter always agree on the device. Chrome answers a bare `{ audio: true }`
 * from its own preference ranking, which can pin a dead built-in mic while the OS default
 * (e.g. connected earbuds) is ignored; asking for the `'default'` alias follows the OS instead.
 */

/** Chrome/Edge expose these as aliases of a real device; they are never a device of their own. */
const ALIAS_IDS = new Set(['default', 'communications']);
const SYSTEM_DEFAULT_ID = 'default';
/** Errors that mean "this device, not the mic in general": the next candidate may still open. */
const FALL_THROUGH = new Set(['OverconstrainedError', 'NotFoundError']);
/** A busy device: worth walking past at start, but a switch must report it and keep the old mic. */
const BUSY = new Set(['NotReadableError', 'AbortError']);

export const isMicAlias = (deviceId: string) => ALIAS_IDS.has(deviceId);

export class MicSwitchError extends Error {
  constructor(
    readonly code: MicSwitchErrorCode,
    message: string,
    cause?: unknown,
  ) {
    super(message, { cause });
    this.name = 'MicSwitchError';
  }
}

export interface OpenedMic {
  stream: MediaStream;
  track: MediaStreamTrack;
  resolvedBy: MicResolution;
  /** The saved choice that could not be opened, when the mic fell back from it. */
  fellBackFrom: MicChoice | null;
}

export interface MicInputs {
  /** Real devices, aliases removed. Labels are empty until the site has mic permission. */
  devices: MediaDeviceInfo[];
  /** Chrome's `'default'` alias (follows the OS default); null where the browser has none. */
  systemDefault: MediaDeviceInfo | null;
}

export async function listMicInputs(): Promise<MicInputs> {
  let all: MediaDeviceInfo[] = [];
  try {
    all = (await navigator.mediaDevices?.enumerateDevices?.()) ?? [];
  } catch {
    all = [];
  }
  const inputs = all.filter((d) => d.kind === 'audioinput' && d.deviceId !== '');
  return {
    devices: inputs.filter((d) => !isMicAlias(d.deviceId)),
    systemDefault: inputs.find((d) => d.deviceId === SYSTEM_DEFAULT_ID) ?? null,
  };
}

const errorName = (err: unknown) => (err instanceof DOMException || err instanceof Error ? err.name : '');

export const stopStream = (stream: MediaStream | null | undefined) =>
  stream?.getTracks().forEach((t) => t.stop());

function constraintsFor(deviceId: string | undefined, sampleRate?: number): MediaStreamConstraints {
  return {
    audio: {
      channelCount: 1,
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      // Firefox only connects a track whose rate matches the graph's.
      ...(sampleRate ? { sampleRate: { ideal: sampleRate } } : {}),
    },
  };
}

function opened(stream: MediaStream, resolvedBy: MicResolution, fellBackFrom: MicChoice | null): OpenedMic {
  const track = stream.getAudioTracks()[0];
  if (!track) {
    stopStream(stream);
    throw new DOMException('The microphone returned no audio track', 'NotReadableError');
  }
  return { stream, track, resolvedBy, fellBackFrom };
}

/**
 * Before the first grant Chrome lists no `'default'` alias, so the bare request lands on its
 * own ranking; once permission exists, move to the OS default if that is a different device.
 */
async function settleOnSystemDefault(mic: OpenedMic): Promise<OpenedMic> {
  const { systemDefault } = await listMicInputs();
  if (!systemDefault || systemDefault.groupId === mic.track.getSettings().groupId) return mic;
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraintsFor(SYSTEM_DEFAULT_ID));
    stopStream(mic.stream);
    return opened(stream, 'system-default', mic.fellBackFrom);
  } catch {
    return mic;
  }
}

interface OpenMicOptions {
  /** Start: walk on to the OS / browser default when the choice is gone. Switch: exact or fail. */
  fallback: boolean;
}

/**
 * Opens the user's choice (by id, then by label, since Safari and cleared site data rotate
 * ids), else the OS default, else the browser's own pick. Always mono.
 */
export async function openMic(choice: MicChoice | null | undefined, { fallback }: OpenMicOptions): Promise<OpenedMic> {
  const { devices, systemDefault } = await listMicInputs();
  const steps: { by: MicResolution; deviceId?: string }[] = [];
  if (choice?.deviceId) steps.push({ by: 'chosen', deviceId: choice.deviceId });
  const byLabel = choice?.label ? devices.find((d) => d.label === choice.label && d.deviceId !== choice.deviceId) : undefined;
  if (byLabel) steps.push({ by: 'chosen-by-label', deviceId: byLabel.deviceId });
  if (!choice || fallback) {
    if (systemDefault) steps.push({ by: 'system-default', deviceId: SYSTEM_DEFAULT_ID });
    steps.push({ by: 'browser-default' });
  }

  let lastError: unknown = new DOMException('No microphone is connected', 'NotFoundError');
  for (const step of steps) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraintsFor(step.deviceId));
      const fellBack = choice && step.by !== 'chosen' && step.by !== 'chosen-by-label' ? choice : null;
      const mic = opened(stream, step.by, fellBack);
      return step.by === 'browser-default' ? await settleOnSystemDefault(mic) : mic;
    } catch (err) {
      lastError = err;
      const name = errorName(err);
      if (!FALL_THROUGH.has(name) && !(fallback && BUSY.has(name))) break;
    }
  }
  throw lastError;
}

export function describeMic(mic: OpenedMic): ActiveMic {
  const settings = mic.track.getSettings();
  return {
    deviceId: settings.deviceId ?? '',
    groupId: settings.groupId ?? '',
    label: mic.track.label,
    resolvedBy: mic.resolvedBy,
  };
}

/** iOS keeps one capture per page: a second `getUserMedia` mutes the first. */
function oneCaptureAtATime(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

const SWITCH_MESSAGES: Record<string, string> = {
  NotAllowedError: 'Microphone access was denied',
  NotReadableError: 'That microphone is in use by another app or unavailable',
  AbortError: 'That microphone could not be started',
  OverconstrainedError: 'That microphone is no longer connected',
  NotFoundError: 'That microphone is no longer connected',
};

function toSwitchError(err: unknown, target: MicChoice | null): MicSwitchError {
  if (err instanceof MicSwitchError) return err;
  const name = target?.label ?? 'that microphone';
  return new MicSwitchError('OPEN_FAILED', SWITCH_MESSAGES[errorName(err)] ?? `Couldn't switch to ${name}`, err);
}

export interface SwitchResult {
  mic: ActiveMic;
  /** Null when nothing changed (an `ended` that a newer switch had already handled). */
  swap: MicSwapEvent | null;
}

interface Attached {
  mic: OpenedMic;
  source: MediaStreamAudioSourceNode;
  onEnded: () => void;
}

/**
 * The live input side of a recording graph: the mic source feeding `sink`, plus an analyser
 * tapped off the same source so a level meter never opens a second capture. Swapping the
 * source leaves everything downstream (encoder, chunks, upload) running as one file.
 */
export class LiveInput {
  readonly analyser: AnalyserNode;
  private active: Attached | null = null;
  private readonly swaps: MicSwapEvent[] = [];
  private queue: Promise<unknown> = Promise.resolve();
  private stopped = false;

  constructor(
    private readonly context: AudioContext,
    private readonly sink: AudioNode,
    private readonly onInputEnded: () => void,
  ) {
    this.analyser = context.createAnalyser();
    this.analyser.fftSize = 2048;
  }

  get mic(): ActiveMic | null {
    return this.active ? describeMic(this.active.mic) : null;
  }

  get stream(): MediaStream | null {
    return this.active?.mic.stream ?? null;
  }

  /** False with no mic, or one the browser reports ended or muted (it delivers no audio). */
  get delivering(): boolean {
    const track = this.active?.mic.track;
    return !!track && track.readyState === 'live' && !track.muted;
  }

  swapEvents(): MicSwapEvent[] {
    return [...this.swaps];
  }

  /** Connects an opened mic; on a rate the graph can't take (Firefox) reopens it at the graph's rate. */
  async attach(mic: OpenedMic): Promise<void> {
    this.connect(await this.sourceFor(mic));
  }

  /** Serialised, so overlapping requests (a toast click during an auto-switch) never interleave. */
  switchTo(choice: MicChoice | null, reason: MicSwitchReason): Promise<SwitchResult> {
    const run = this.queue.then(() => this.doSwitch(choice, reason));
    this.queue = run.catch(() => undefined);
    return run;
  }

  stop(): void {
    this.stopped = true;
    if (this.active) this.release(this.active);
    this.active = null;
    this.analyser.disconnect();
  }

  private async doSwitch(choice: MicChoice | null, reason: MicSwitchReason): Promise<SwitchResult> {
    if (this.stopped) throw new MicSwitchError('NOT_RECORDING', 'Nothing is being recorded');
    const previous = this.active;
    // An `ended` queued behind a switch that already replaced the dead mic must not undo it.
    if (reason === 'disconnected' && previous?.mic.track.readyState === 'live') {
      return { mic: describeMic(previous.mic), swap: null };
    }
    const previousLabel = previous?.mic.track.label ?? null;
    const previousDeviceId = previous?.mic.track.getSettings().deviceId;
    // A dead track has nothing to overlap with; iOS can't hold two captures at once.
    const breakFirst = !previous || previous.mic.track.readyState === 'ended' || oneCaptureAtATime();
    if (previous && breakFirst) {
      this.release(previous);
      this.active = null;
    }
    try {
      const next = await this.sourceFor(await openMic(choice, { fallback: false }));
      if (this.stopped) {
        this.dispose(next);
        throw new MicSwitchError('NOT_RECORDING', 'Nothing is being recorded');
      }
      this.connect(next);
      if (previous && !breakFirst) this.release(previous);
      return { mic: describeMic(next.mic), swap: this.recordSwap(previousLabel, next.mic.track.label, reason) };
    } catch (err) {
      if (previous && breakFirst && !this.stopped) await this.restore(previousDeviceId, previousLabel);
      throw toSwitchError(err, choice);
    }
  }

  /** Break-before-make failed: put a mic back so the recording keeps capturing. */
  private async restore(deviceId: string | undefined, label: string | null): Promise<void> {
    try {
      const back = await this.sourceFor(await openMic(deviceId ? { deviceId } : null, { fallback: true }));
      if (this.stopped) {
        this.dispose(back);
        return;
      }
      this.connect(back);
      if (back.mic.track.label !== label) this.recordSwap(label, back.mic.track.label, 'fallback');
    } catch {
      this.active = null;
    }
  }

  private recordSwap(from: string | null, to: string, reason: MicSwitchReason): MicSwapEvent {
    const swap = { at: new Date().toISOString(), from, to, reason };
    this.swaps.push(swap);
    return swap;
  }

  private async sourceFor(mic: OpenedMic): Promise<Attached> {
    try {
      return this.wrap(mic, this.context.createMediaStreamSource(mic.stream));
    } catch (err) {
      stopStream(mic.stream);
      if (errorName(err) !== 'NotSupportedError') throw err;
    }
    const stream = await navigator.mediaDevices.getUserMedia(
      constraintsFor(mic.track.getSettings().deviceId, this.context.sampleRate),
    );
    const retried = opened(stream, mic.resolvedBy, mic.fellBackFrom);
    try {
      return this.wrap(retried, this.context.createMediaStreamSource(stream));
    } catch (err) {
      stopStream(stream);
      throw new MicSwitchError('RATE_MISMATCH', `Can't switch to ${mic.track.label || 'that microphone'} in this browser`, err);
    }
  }

  private wrap(mic: OpenedMic, source: MediaStreamAudioSourceNode): Attached {
    const attached: Attached = {
      mic,
      source,
      onEnded: () => {
        if (this.active === attached && !this.stopped) this.onInputEnded();
      },
    };
    return attached;
  }

  private connect(next: Attached): void {
    next.source.connect(this.sink);
    next.source.connect(this.analyser);
    next.mic.track.addEventListener('ended', next.onEnded);
    this.active = next;
    // Died while the graph was being built (before the listener existed): recover once the
    // caller has finished starting, so the take doesn't run on silence.
    if (next.mic.track.readyState === 'ended') setTimeout(next.onEnded, 0);
  }

  private release(attached: Attached): void {
    attached.mic.track.removeEventListener('ended', attached.onEnded);
    attached.source.disconnect();
    stopStream(attached.mic.stream);
  }

  private dispose(attached: Attached): void {
    attached.source.disconnect();
    stopStream(attached.mic.stream);
  }
}
